import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);
  // Visibility state - starts true, set to false on dismiss
  const [isVisible, setIsVisible] = useState(true);
  // State to track if data fetch has completed (or failed)
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset loading state on mount
    setIsLoading(true);
    // We will always try to fetch on mount now, no storage check needed here

    const fetchAnnouncement = async () => {
      try {
        const response = await fetch(
          'https://causeandimpact-assets.s3.us-east-1.amazonaws.com/Website/announcements.json'
        );
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          // Set announcement to null or an error state if needed
          setAnnouncement(null);
        } else {
          const data = await response.json();
          setAnnouncement(data);
        }
      } catch (error) {
        console.error('Error fetching announcement:', error);
        setAnnouncement(null); // Ensure announcement is null on error
      } finally {
        // Mark loading as complete regardless of success/failure
        setIsLoading(false);
      }
    };

    fetchAnnouncement();

  }, []); // Empty dependency array: run only once on mount

  const handleDismiss = () => {
    // Simply set the state to hide the banner for this instance
    setIsVisible(false);
    // NO sessionStorage.setItem needed anymore
  };

  // --- Determine if the banner should render ---
  // 1. Check if loading is complete AND
  // 2. Check if it hasn't been dismissed in this instance (isVisible is true) AND
  // 3. Check if there's announcement data AND
  // 4. Check if the announcement is marked active in the data
  const shouldRender = !isLoading && isVisible && announcement && announcement.active;

  // Return null if any condition isn't met
  if (!shouldRender) {
    return null;
  }

  // --- Render the banner ---
  // (JSX remains the same as before)
  return (
    <div style={{
      width: '100%',
      background: 'linear-gradient(to right, #2d6df6, #6e3df3)',
      padding: '14px 0',
      position: 'relative',
      animation: 'fadeInSlideDown 0.6s ease-out',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden'
    }}>
      <style>
        {`
          /* Keyframes and CSS remain the same */
          @keyframes fadeInSlideDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulseGlow {
            0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(255, 255, 255, 0)); }
            50% { transform: scale(1.1); filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.5)); }
          }
          @keyframes glowRing {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
            70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
          }

          .announcement-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 40px 0 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 20px;
            position: relative;
          }

          .announcement-content {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-grow: 1;
            justify-content: center;
            color: white;
            font-size: 1.0rem;
            font-weight: 600;
            line-height: 1.4;
            text-align: center;
            text-shadow: 0 1px 2px rgba(0,0,0,0.1);
            min-width: 0;
          }

          .alert-icon-wrapper {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: relative;
            margin-right: 8px;
            animation: glowRing 2s infinite;
            border-radius: 50%;
            flex-shrink: 0;
          }

          .alert-icon {
            font-size: 1.4rem;
            display: inline-block;
            animation: pulseGlow 3s infinite;
            transform-origin: center;
            filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.3));
            position: relative;
            padding: 4px;
            line-height: 1;
          }

          .announcement-cta {
            display: inline-flex;
            align-items: center;
            background-color: white;
            color: #4b2eff;
            padding: 8px 20px;
            border-radius: 999px;
            font-size: 0.9rem;
            font-weight: 700;
            text-decoration: none;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            white-space: nowrap;
            border: 1px solid rgba(255,255,255,0.2);
            flex-shrink: 0;
            cursor: pointer;
          }
          .announcement-cta:hover {
            transform: translateY(-1px);
            box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
          }

          .announcement-close-btn {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            cursor: pointer;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: bold;
            line-height: 1;
            padding: 0;
            transition: background-color 0.2s ease;
            z-index: 10;
          }
          .announcement-close-btn:hover {
            background-color: rgba(255, 255, 255, 0.25);
          }

          /* --- Mobile Styles --- */
          @media (max-width: 768px) {
            .announcement-container {
              flex-direction: column;
              gap: 12px;
              padding: 10px 40px 15px 15px;
              text-align: center;
            }
            .announcement-content {
              font-size: 0.95rem;
              text-align: center;
              margin-bottom: 5px;
            }
            .alert-icon-wrapper { margin-right: 6px; }
            .alert-icon { font-size: 1.2rem; }
            .announcement-cta {
              padding: 10px 24px;
              font-size: 0.95rem;
              margin: 5px auto 0 auto;
            }
            .announcement-close-btn {
              right: 10px;
              top: 10px;
              transform: none;
            }
          }

          @media (max-width: 480px) {
             .announcement-content { font-size: 0.9rem; }
             .announcement-cta { padding: 8px 18px; font-size: 0.9rem; }
             .alert-icon-wrapper { margin-right: 4px; }
             .alert-icon { font-size: 1.1rem; }
             .announcement-close-btn {
                 right: 8px;
                 top: 8px;
                 width: 22px;
                 height: 22px;
                 font-size: 14px;
             }
             .announcement-container { padding: 8px 35px 10px 10px; }
           }
        `}
      </style>

      <div className="announcement-container">
        <div className="announcement-content">
          <div className="alert-icon-wrapper">
            <span className="alert-icon">🚨</span>
          </div>
          <span>{announcement.message}</span>
        </div>
        {announcement.ctaText && announcement.ctaLink && (
          <Link to={announcement.ctaLink} className="announcement-cta">
            {announcement.ctaText}
          </Link>
        )}
        <button onClick={handleDismiss} className="announcement-close-btn" aria-label="Dismiss announcement">
          ×
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;